import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useOnboardingState } from 'hooks/useOnboardingState';
import { Section, SectionHeader } from 'components/ds';
import LadderGameModal from 'components/common/LadderGameModal';
import BottleSpinModal, { BottleParticipant } from 'components/common/BottleSpinModal';
import ParticipantSourceSheet, { ParticipantItem } from 'components/common/ParticipantSourceSheet';
import { LadderIcon } from 'components/common/LadderIcons';
import shotCreate from 'assets/guide/create.webp';
import shotInvite from 'assets/guide/invite.webp';
import shotCost from 'assets/guide/cost.webp';
import shotSchedule from 'assets/guide/schedule.webp';
import shotLocation from 'assets/guide/location.webp';
import shotGame from 'assets/guide/game.webp';
import styles from './HomeGuide.module.css';
import { IconChevronRight } from 'components/ds';

/**
 * 홈 가이드 — 시작 가이드 · 빠른 시작 · 오늘의 팁 · 미니게임 · 시작 포인터.
 *
 * 왜 한 파일인가
 *   이전에는 OnboardingStack / OnboardingChecklist / ScenarioQuickStart /
 *   DailyFeatureBanner / MiniGamesSection / StartPointer 여섯 컴포넌트로
 *   흩어져 있었고, 그중 Stack 과 Checklist 는 "모임 만들기 · 친구 초대 ·
 *   정산 · 미니게임" 을 양쪽에서 각각 안내하는 중복이었습니다.
 *   여기서는 안내를 한 줄기(StartGuide)로 합치고, 형태는 참고 앱의
 *   헤어라인 목록 · 칩 · 테두리 카드만 씁니다.
 *
 * 게임 모달(사다리·병돌리기·참여자 선택)은 기존 것을 그대로 재사용합니다.
 * 바뀐 건 진입 UI 뿐이고 게임 자체는 손대지 않았습니다.
 */

/* ── 인라인 아이콘 (이모지 금지) ── */

const IcX = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.4" strokeLinecap="round" aria-hidden>
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);
const IcArrowLeft = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M15 18l-6-6 6-6" />
    </svg>
);
const IcArrowRight = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M9 18l6-6-6-6" />
    </svg>
);
const IcCheck = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M4 12.5l5.5 5.5L20 7" />
    </svg>
);
const IcFork = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M7 2v7a2 2 0 0 0 4 0V2M9 9v13M17 2c-1.5 2-2 4-2 6v3h4V8c0-2-.5-4-2-6zM17 11v11" />
    </svg>
);
const IcCup = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M4 8h13v6a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5zM17 9h2a2 2 0 0 1 0 5h-2M6 2v3M10 2v3M14 2v3" />
    </svg>
);
const IcGlass = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M6 3h9l-1 8a3.5 3.5 0 0 1-7 0zM10.5 19v2M8 21h5M17 6h2a2 2 0 0 1 0 5h-2" />
    </svg>
);
const IcPlane = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M2 13l20-7-7 20-3-8-8-3z" />
    </svg>
);
const IcBook = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M4 4h7a3 3 0 0 1 3 3v13a2 2 0 0 0-2-2H4zM20 4h-3a3 3 0 0 0-3 3v13a2 2 0 0 1 2-2h4z" />
    </svg>
);
const IcRun = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <circle cx="16" cy="4" r="2" /><path d="M12 21l2-6-3-3 1-5 3 3 3 1M8 13l-2 3-3 1" />
    </svg>
);
const IcBottle = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M10 2h4v4c0 1.5 2 3 2 5v11a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2V11c0-2 2-3.5 2-5z" />
        <path d="M8 14h8" />
    </svg>
);

/* ══════════════════════════════════════════════════════════════════
 * StartGuide — 시작 가이드
 *
 * 예전 OnboardingChecklist(4단계)와 OnboardingStack(6장 카드)을 하나로
 * 합쳤습니다. 완료 판정은 Checklist 가 쓰던 규칙 그대로입니다.
 * ════════════════════════════════════════════════════════════════ */

interface StartGuideProps {
    meetings: any[];
    /** 내가 지출을 1건이라도 입력했는지 */
    hasMyPayment?: boolean;
    userNo?: number | string;
    /** 미니게임 자리로 스크롤 */
    onGoGames?: () => void;
}

export const StartGuide = ({ meetings, hasMyPayment, userNo, onGoGames }: StartGuideProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { isNewUser, ready, isDismissed, dismiss, isCompleted } = useOnboardingState();

    /* 지금 이 사람이 어디까지 해봤는지. 아직 안 해본 것이 먼저 나옵니다 —
       처음 온 사람에게 필요한 건 할 일 목록이 아니라 "이게 뭐 하는 앱인지" 입니다. */
    const cards = useMemo(() => {
        const mine = meetings.filter((m) => {
            const c = m.creatorNo ?? m.hostNo ?? m.createdByNo;
            return c && Number(c) === Number(userNo);
        });
        const done = {
            create: mine.length > 0 || isCompleted('step_create'),
            invite: mine.some((m) => Number(m.memberCount || 0) > 1) || isCompleted('step_invite'),
            schedule: meetings.some((m) => m.hasActiveProposal || m.activeProposalId
                || Number(m.proposalVoteCount || 0) > 0) || isCompleted('step_schedule'),
            cost: !!hasMyPayment
                || meetings.some((m) => Number(m.myPaidAmount || 0) > 0)
                || isCompleted('step_cost'),
        };
        const latest = mine[0]?.id ?? meetings[0]?.id;

        const all = [
            {
                id: 'create',
                shot: shotCreate,
                done: done.create,
                go: () => navigate('/meeting/create'),
            },
            {
                id: 'invite',
                shot: shotInvite,
                done: done.invite,
                go: () => navigate(latest ? `/meeting/detail/${latest}` : '/meetings'),
            },
            {
                id: 'schedule',
                shot: shotSchedule,
                done: done.schedule,
                go: () => navigate('/schedule/select'),
            },
            {
                id: 'cost',
                shot: shotCost,
                done: done.cost,
                go: () => navigate(latest ? `/cost/${latest}` : '/meetings'),
            },
            {
                id: 'location',
                shot: shotLocation,
                done: false,
                go: () => navigate('/meetings'),
            },
            {
                id: 'minigame',
                shot: shotGame,
                done: false,
                go: () => onGoGames?.(),
            },
        ];
        /* 이미 해본 것은 빼고, 지금 그 사람의 상황인 것만 남깁니다.
           해본 단계까지 보여주면 "아직 한 번도 안 하셨군요" 가 이미 해본 사람에게
           뜹니다. 가이드는 할 일 목록이 아니라 지금 필요한 이야기여야 합니다. */
        return all.filter((c) => !c.done);
    }, [meetings, hasMyPayment, userNo, isCompleted, navigate, onGoGames]);

    /** 장마다 딸린 설명 줄 — 번역이 비어 있으면 아무것도 그리지 않습니다 */
    const pointsOf = useCallback((id: string): string[] => {
        const v = t(`startGuide.${id}.points`, { returnObjects: true }) as unknown;
        return Array.isArray(v) ? (v as string[]).filter((s) => typeof s === 'string') : [];
    }, [t]);

    const [open, setOpen] = useState(true);
    const [idx, setIdx] = useState(0);
    const deckRef = useRef<HTMLDivElement>(null);
    const cardCountRef = useRef(0);
    cardCountRef.current = cards.length;

    /* 어느 장을 보고 있는지 — 스크롤 위치로 읽습니다.
       손가락으로 넘기는 것과 점·화살표로 넘기는 것이 같은 상태를 봅니다. */
    const onScroll = useCallback(() => {
        const el = deckRef.current;
        if (!el) return;
        const next = Math.round(el.scrollLeft / Math.max(1, el.clientWidth));
        setIdx((prev) => (prev === next ? prev : next));
    }, []);

    const goTo = useCallback((i: number) => {
        const el = deckRef.current;
        if (!el) return;
        el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
    }, []);

    /* 화살표는 state 가 아니라 지금 스크롤 위치를 기준으로 움직입니다 —
       사진이 늦게 로드되며 스크롤이 흔들리면 state 가 실제 위치와 어긋납니다. */
    const step = useCallback((delta: number) => {
        const el = deckRef.current;
        if (!el) return;
        const cur = Math.round(el.scrollLeft / Math.max(1, el.clientWidth));
        goTo(Math.min(Math.max(0, cur + delta), cardCountRef.current - 1));
    }, [goTo]);

    if (!ready || !isNewUser || isDismissed('start_guide') || !open) return null;
    /* 다 해본 사람에게는 가르칠 것이 없습니다 */
    if (cards.length === 0) return null;

    const go = (fn: () => void) => { setOpen(false); fn(); };

    return (
        <div className={styles.modalOverlay} onClick={() => setOpen(false)}>
            <div
                className={styles.modal}
                role="dialog"
                aria-modal="true"
                aria-label={t('startGuide.title')}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.modalHead}>
                    <h2 className={styles.modalTitle}>{t('startGuide.title')}</h2>
                    <button
                        type="button"
                        className={styles.modalClose}
                        onClick={() => setOpen(false)}
                        aria-label={t('common.close')}
                    >
                        <IcX />
                    </button>
                </div>

                {/* 좌우로 넘겨 봅니다 — 스크롤 스냅이라 손가락 동작이 자연스럽습니다 */}
                <div className={styles.deck} ref={deckRef} onScroll={onScroll}>
                    {cards.map((c) => (
                        <section key={c.id} className={styles.card}>
                            {c.shot ? (
                                <span className={styles.shotWrap}>
                                    <img
                                        className={styles.shot}
                                        src={c.shot}
                                        alt=""
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </span>
                            ) : null}
                            <span className={styles.cardHeadline}>
                                {t(`startGuide.${c.id}.headline`)}
                            </span>
                            <span className={styles.cardDesc}>
                                {t(`startGuide.${c.id}.desc`)}
                            </span>
                            {/* 설명만으로는 "그래서 나한테 뭐가 좋은데" 가 안 옵니다 */}
                            <ul className={styles.cardPoints}>
                                {pointsOf(c.id).map((p) => (
                                    <li key={p} className={styles.cardPoint}>
                                        <IcCheck />
                                        <span>{p}</span>
                                    </li>
                                ))}
                            </ul>
                            <button
                                type="button"
                                className={styles.cardCta}
                                onClick={() => go(c.go)}
                            >
                                {t(`startGuide.${c.id}.cta`)}
                            </button>
                        </section>
                    ))}
                </div>

                <div className={styles.deckNav}>
                    <button
                        type="button"
                        className={styles.deckArrow}
                        onClick={() => step(-1)}
                        disabled={idx === 0}
                        aria-label={t('startGuide.prev')}
                    >
                        <IcArrowLeft />
                    </button>
                    <span className={styles.dots}>
                        {cards.map((c, i) => (
                            <button
                                key={c.id}
                                type="button"
                                className={i === idx ? `${styles.dot} ${styles.dotOn}` : styles.dot}
                                onClick={() => goTo(i)}
                                aria-label={`${i + 1} / ${cards.length}`}
                            />
                        ))}
                    </span>
                    <button
                        type="button"
                        className={styles.deckArrow}
                        onClick={() => step(1)}
                        disabled={idx === cards.length - 1}
                        aria-label={t('startGuide.next')}
                    >
                        <IcArrowRight />
                    </button>
                </div>

                <div className={styles.modalFoot}>
                    <button
                        type="button"
                        className={styles.modalNever}
                        onClick={() => { dismiss('start_guide'); setOpen(false); }}
                    >
                        {t('homeComp.neverShow')}
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ══════════════════════════════════════════════════════════════════
 * QuickStart — 시나리오 하나를 고르면 만들기 2단계로 미리 채워 이동
 * ════════════════════════════════════════════════════════════════ */

const SCENARIOS = [
    { id: 'dinner', key: 'scnDinner', Icon: IcFork, prefill: { meetingType: 'DAY', category: 'FOOD' } },
    { id: 'brunch', key: 'scnBrunch', Icon: IcCup, prefill: { meetingType: 'DAY', category: 'CAFE' } },
    { id: 'beer', key: 'scnDrink', Icon: IcGlass, prefill: { meetingType: 'DAY', category: 'BEER' } },
    { id: 'travel', key: 'scnTravel', Icon: IcPlane, prefill: { meetingType: 'TRAVEL' } },
    { id: 'study', key: 'scnStudy', Icon: IcBook, prefill: { meetingType: 'DAY', category: 'STUDY' } },
    { id: 'exercise', key: 'scnExercise', Icon: IcRun, prefill: { meetingType: 'DAY', category: 'EXERCISE' } },
];

export const QuickStart = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { isNewUser, ready, isDismissed, dismiss } = useOnboardingState();

    if (!ready || !isNewUser || isDismissed('quick_start')) return null;

    const go = (s: typeof SCENARIOS[number]) => {
        const label = t(`homeComp.${s.key}`);
        const prefill = { ...s.prefill, meetingName: label };
        // 만들기 화면이 새로고침돼도 값이 남도록 세션에 먼저 적어 둡니다
        try {
            const existing = JSON.parse(sessionStorage.getItem('meeting_create_data') || '{}');
            sessionStorage.setItem('meeting_create_data', JSON.stringify({
                ...existing, ...prefill, fromQuickstart: true,
            }));
        } catch { /* 세션 못 쓰면 state 로만 넘깁니다 */ }
        navigate('/meeting/create/step2', { state: { ...prefill, fromQuickstart: true } });
    };

    /* 섹션 껍데기를 이 안에서 그립니다.
       바깥(home)에 두면 숨겼을 때 제목과 구분 띠만 덩그러니 남습니다. */
    return (
        <Section divided>
            <div className={styles.head}>
                <h2 className={styles.headTitle}>{t('homeComp.scnTitle')}</h2>
                <button type="button" className={styles.headDismiss} onClick={() => dismiss('quick_start')}>
                    {t('homeComp.hide')}
                </button>
            </div>
            <div className={styles.chips}>
                {SCENARIOS.map(s => (
                    <button key={s.id} type="button" className={styles.chip} onClick={() => go(s)}>
                        <span className={styles.chipIcon}><s.Icon /></span>
                        {t(`homeComp.${s.key}`)}
                    </button>
                ))}
            </div>
        </Section>
    );
};

/* ══════════════════════════════════════════════════════════════════
 * DailyTip — 하루에 한 가지 기능만 소개합니다
 * ════════════════════════════════════════════════════════════════ */

const TIPS = [
    { id: 'race', key: 'dfbRace', go: (nav: any) => nav('/meetings') },
    { id: 'roulette', key: 'dfbRoulette', go: (nav: any) => nav('/meetings') },
    { id: 'ladder', key: 'dfbLadder', games: true },
    { id: 'bottle', key: 'dfbBottle', games: true },
    { id: 'vote', key: 'dfbVote', go: (nav: any) => nav('/schedule/select') },
    { id: 'travel', key: 'dfbTravel', go: (nav: any) => nav('/meeting/create') },
    { id: 'chat', key: 'dfbChat', go: (nav: any) => nav('/chat') },
    { id: 'cost', key: 'dfbCost', go: (nav: any) => nav('/meetings') },
    { id: 'location', key: 'dfbLocation', go: (nav: any) => nav('/meetings') },
    { id: 'minigames', key: 'dfbMinigames', games: true },
];

const todayKey = () => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
};

export const DailyTip = ({ onGoGames }: { onGoGames?: () => void }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { isNewUser, ready, daysSinceFirstSeen, isDismissed, dismiss } = useOnboardingState();

    if (!ready || !isNewUser) return null;

    const tip = TIPS[Math.abs(daysSinceFirstSeen) % TIPS.length];
    const dismissKey = `daily_${todayKey()}_${tip.id}`;
    if (isDismissed(dismissKey)) return null;

    const act = () => (tip.games ? onGoGames?.() : tip.go?.(navigate));

    /* 제목까지 이 안에서 그립니다 — 숨기면 섹션이 통째로 사라져야 합니다 */
    return (
        <Section divided>
            <SectionHeader title={t('homeComp.tipSection')} />
            <div className={styles.tip}>
                <button type="button" className={styles.tipMain} onClick={act}>
                    <span className={styles.tipTag}>TIP · Day {daysSinceFirstSeen + 1}</span>
                    <span className={styles.tipTitle}>{t(`homeBanner.${tip.key}T`)}</span>
                    <span className={styles.tipDesc}>{t(`homeBanner.${tip.key}D`)}</span>
                </button>
                <div className={styles.tipFoot}>
                    <button type="button" className={styles.stepCta} onClick={act}>
                        {t(`homeBanner.${tip.key}C`)}
                    </button>
                    <button type="button" className={styles.headDismiss} onClick={() => dismiss(dismissKey)}>
                        {t('homeComp.hide')}
                    </button>
                </div>
            </div>
        </Section>
    );
};

/* ══════════════════════════════════════════════════════════════════
 * HomeGames — 모임 없이 바로 하는 미니게임
 *
 * 진입 UI 만 새로 짰고, 게임 모달과 참여자 선택 시트는 기존 것을 그대로 씁니다.
 * ════════════════════════════════════════════════════════════════ */

export const HomeGames = React.forwardRef<HTMLDivElement>((_props, ref) => {
    const { t } = useTranslation();
    const [showLadder, setShowLadder] = useState(false);
    const [showBottle, setShowBottle] = useState(false);
    // 홈에는 모임 참여자 맥락이 없으므로 누구와 할지 먼저 고릅니다
    const [pickerFor, setPickerFor] = useState<null | 'bottle' | 'ladder'>(null);
    const [picked, setPicked] = useState<ParticipantItem[] | null>(null);

    const onPick = (list: ParticipantItem[]) => {
        const game = pickerFor;
        setPicked(list);
        setPickerFor(null);
        if (game === 'bottle') setShowBottle(true);
        else if (game === 'ladder') setShowLadder(true);
    };

    const games = [
        {
            id: 'bottle',
            icon: <IcBottle />,
            title: t('games.bottleTitle'),
            desc: t('games.pickPenalty'),
        },
        {
            id: 'ladder',
            icon: <LadderIcon size={22} color="currentColor" />,
            title: t('games.ladderTitle'),
            desc: t('games.leadOrder'),
        },
    ] as const;

    return (
        <div ref={ref}>
            <div className={styles.games}>
                {games.map(g => (
                    <button
                        key={g.id}
                        type="button"
                        className={styles.game}
                        onClick={() => { setPicked(null); setPickerFor(g.id as 'bottle' | 'ladder'); }}
                    >
                        <span className={styles.gameIcon}>{g.icon}</span>
                        <span className={styles.gameBody}>
                            <span className={styles.gameTitle}>{g.title}</span>
                            <span className={styles.gameDesc}>{g.desc}</span>
                        </span>
                        <span className={styles.gameArrow}><IconChevronRight /></span>
                    </button>
                ))}
            </div>

            <ParticipantSourceSheet
                open={pickerFor !== null}
                onClose={() => setPickerFor(null)}
                onPick={onPick}
            />
            <LadderGameModal
                open={showLadder}
                participants={(picked || []) as BottleParticipant[]}
                onClose={() => setShowLadder(false)}
            />
            <BottleSpinModal
                open={showBottle}
                initialParticipants={(picked || []) as BottleParticipant[]}
                onClose={() => setShowBottle(false)}
            />
        </div>
    );
});
HomeGames.displayName = 'HomeGames';

/* ══════════════════════════════════════════════════════════════════
 * StartPointer — 모임이 하나도 없는 사람에게 만들기 버튼을 가리킵니다
 * ════════════════════════════════════════════════════════════════ */

export const StartPointer = ({ hasMeetings }: { hasMeetings: boolean }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { isNewUser, ready, isDismissed, dismiss } = useOnboardingState();

    if (!ready || !isNewUser || hasMeetings || isDismissed('start_pointer')) return null;

    return (
        <div className={styles.pointer}>
            <span
                className={styles.pointerText}
                role="button"
                tabIndex={0}
                onClick={() => navigate('/meeting/create')}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate('/meeting/create'); }}
            >
                {t('homeComp.pointerStart')}
            </span>
            <button
                type="button"
                className={styles.pointerClose}
                onClick={() => dismiss('start_pointer')}
                aria-label={t('common.close')}
            >
                <IcX />
            </button>
        </div>
    );
};
